import { useMemo } from 'react';

import { composeChannels } from './composeChannels';
import { useChannelsPersonalization } from './personalization';
import { useChannelsSettings } from './settings';
import { useChannelAssets } from './useChannelAssets';
import { useChannelCollection } from './useChannelCollection';
import { useEntitledChannels } from './useEntitledChannels';
import { useSyncWithLastWatchStore } from './useSyncWithLastWatchStore';

type UseChannelsProps = {
  pageReferenceId?: string;
  referenceId: string;
};

export function useChannels({ referenceId, pageReferenceId }: UseChannelsProps) {
  const { settings, error: settingsError } = useChannelsSettings({ referenceId });

  const { channelAssetsMap } = useChannelAssets();
  const { entitledChannels } = useEntitledChannels();
  const { channelCollection, isLoading: isLoadingCollection, refetch, error } = useChannelCollection({ settings });

  const { personalization, error: personalizationError } = useChannelsPersonalization({
    referenceId,
    pageReferenceId,
  });

  useSyncWithLastWatchStore({ pageReferenceId });

  const channelsError = settingsError ?? personalizationError ?? error;
  const isLoading = !channelsError && (!personalization || isLoadingCollection);

  const channels = useMemo(() => {
    if (!settings || !personalization) {
      return;
    }

    // these are common data needed to build any lineup
    if (!channelAssetsMap || !entitledChannels || !channelCollection) {
      return;
    }

    const channels = composeChannels({
      channelAssetsMap,
      channelCollection,
      entitledChannels,
      personalization,
      settings,
    });

    if (settings.limit) {
      return channels.slice(0, settings.limit);
    }

    return channels;
  }, [settings, personalization, channelAssetsMap, entitledChannels, channelCollection]);

  return { channels, isLoading, error: channelsError, refetch };
}
